<template>
  <div class="settings-tab">
    <n-space vertical :size="24" style="padding: 24px;">
      <!-- User Profile Section -->
      <n-card title="Profile Information">
        <n-descriptions :column="1" bordered>
          <n-descriptions-item label="Email">
            {{ currentUser?.email || '-' }}
          </n-descriptions-item>
          <n-descriptions-item label="Name">
            {{ currentUser?.name || '-' }}
          </n-descriptions-item>
          <n-descriptions-item label="Role">
            <n-tag :type="getRoleType(currentUser?.role || '')">
              {{ (currentUser?.role || '').toUpperCase() }}
            </n-tag>
          </n-descriptions-item>
          <n-descriptions-item label="Created">
            {{ currentUser?.createdAt ? new Date(currentUser.createdAt).toLocaleString() : '-' }}
          </n-descriptions-item>
        </n-descriptions>
      </n-card>

      <!-- Password Change Section -->
      <n-card title="Change Password">
        <n-form
          ref="passwordFormRef"
          :model="passwordForm"
          :rules="passwordRules"
          label-placement="left"
          label-width="140"
          style="max-width: 500px;"
        >
          <n-form-item label="Current Password" path="currentPassword">
            <n-input
              v-model:value="passwordForm.currentPassword"
              type="password"
              placeholder="Enter current password"
              show-password-on="click"
            />
          </n-form-item>
          <n-form-item label="New Password" path="newPassword">
            <n-input
              v-model:value="passwordForm.newPassword"
              type="password"
              placeholder="Enter new password"
              show-password-on="click"
            />
          </n-form-item>
          <n-form-item label="Confirm Password" path="confirmPassword">
            <n-input
              v-model:value="passwordForm.confirmPassword"
              type="password"
              placeholder="Confirm new password"
              show-password-on="click"
            />
          </n-form-item>
          <n-form-item>
            <n-button
              type="primary"
              @click="handleChangePassword"
              :loading="changingPassword"
            >
              Change Password
            </n-button>
          </n-form-item>
        </n-form>
      </n-card>
    </n-space>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useMessage } from 'naive-ui'
import { apiClient, type User } from '@/services/api'
import type { FormInst, FormRules } from 'naive-ui'

const message = useMessage()

// State
const currentUser = ref<User | null>(null)
const passwordFormRef = ref<FormInst | null>(null)
const changingPassword = ref(false)
const passwordForm = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
})

// Password validation rules
const passwordRules: FormRules = {
  currentPassword: [
    { required: true, message: 'Current password is required', trigger: 'blur' },
  ],
  newPassword: [
    { required: true, message: 'New password is required', trigger: 'blur' },
    { min: 6, message: 'Password must be at least 6 characters', trigger: 'blur' },
  ],
  confirmPassword: [
    { required: true, message: 'Please confirm your password', trigger: 'blur' },
    {
      validator: (_rule, value) => {
        return value === passwordForm.value.newPassword
      },
      message: 'Passwords do not match',
      trigger: ['blur', 'input'],
    },
  ],
}

// Methods
const loadCurrentUser = async () => {
  try {
    currentUser.value = await apiClient.getCurrentUser() as User
  } catch (error: any) {
    message.error('Failed to load user information')
  }
}

const handleChangePassword = async () => {
  if (!passwordFormRef.value) return

  try {
    await passwordFormRef.value.validate()

    if (passwordForm.value.newPassword !== passwordForm.value.confirmPassword) {
      message.error('Passwords do not match')
      return
    }

    changingPassword.value = true
    await apiClient.changePassword(
      passwordForm.value.currentPassword,
      passwordForm.value.newPassword
    )

    message.success('Password changed successfully')
    
    // Reset form
    passwordForm.value = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    }
  } catch (error: any) {
    const errorMessage = error.response?.data?.error || error.message || 'Failed to change password'
    message.error(errorMessage)
  } finally {
    changingPassword.value = false
  }
}

const getRoleType = (role: string): 'default' | 'info' | 'success' | 'warning' | 'error' => {
  const roleTypes: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
    admin: 'error',
    moderator: 'warning',
    user: 'success',
  }
  return roleTypes[role] || 'default'
}

// Lifecycle
onMounted(() => {
  loadCurrentUser()
})
</script>

<style scoped>
.settings-tab {
  height: 100%;
  overflow-y: auto;
}
</style>

